import {
  HIDE_LAYOUT_NAVIGATION,
  LAYOUT_TOGGLE_FULL_SCREEN_OVERLAY,
  LAYOUT_WRAPPER_SET_BOTTLE_LINK,
  LAYOUT_WRAPPER_SET_PAGE_DATA,
  SET_LAYOUT_FLAVOUR,
  LAYOUT_WRAPPER_SET_URL,
  SET_LAYOUT_PAGE_ID,
  SHOW_LAYOUT_NAVIGATION,
  SET_LAYOUT_CONTROLLER,
  SET_LAYOUT_BROWSER,
  SET_LAYOUT_IS_TOUCH_DEVICE,
  SET_LAYOUT_HEADER_HEIGHT,
  SET_WINDOW_HEIGHT,
  SET_WINDOW_WIDTH,
  SET_DEVICE_TYPE,
  SET_BROWSER_IS_IE,
  SET_BROWSER_IS_IOS
} from './types'
import {
  DESKTOP_MIN_WIDTH,
  LARGE_DESKTOP_MIN_WIDTH,
  MOBILE_MAX_WIDTH,
  PAGE_MAX_WIDTH,
  TABLET_MAX_WIDTH
} from 'styles/config/config'

import { getHistoryState } from 'libraries/front-end-core/core/history'
import { scrollTo } from 'libraries/front-end-core/helpers/document'
import { detect } from 'detect-browser' // type definitions are not found

import { setSearchHelperVisible, setSearchQuery } from './search'

import { pageToggleRight, setIsMouseAbilityOpen, setPageView } from './page'
import { QA_CONTENT } from 'constants/controllers'
import { JSXElementConstructor } from 'react'
import { Flavour } from 'types/Flavour'
import { AppDispatch, RootState } from 'redux/store'
import { LayoutState } from 'redux/reducers/layout'
import { LayoutPageData } from 'interfaces/layout'

export const showNavigation = (): { type: typeof SHOW_LAYOUT_NAVIGATION } => ({
  type: SHOW_LAYOUT_NAVIGATION
})

export const hideNavigation = (): { type: typeof HIDE_LAYOUT_NAVIGATION } => ({
  type: HIDE_LAYOUT_NAVIGATION
})

export const setLayoutPageId = (pageId: string): { type: typeof SET_LAYOUT_PAGE_ID, pageId: string } => ({
  type: SET_LAYOUT_PAGE_ID,
  pageId
})

export const toggleFullscreenOverlay = (fullscreenOverlay: JSXElementConstructor<any>): { type: typeof LAYOUT_TOGGLE_FULL_SCREEN_OVERLAY, fullscreenOverlay: JSXElementConstructor<any> } => ({
  type: LAYOUT_TOGGLE_FULL_SCREEN_OVERLAY,
  fullscreenOverlay
})

export const layoutWrapperSetUrl = (url: string): { type: typeof LAYOUT_WRAPPER_SET_URL, url: string } => ({
  type: LAYOUT_WRAPPER_SET_URL,
  url
})

export const setLayoutFlavour = (flavour: Flavour): { type: typeof SET_LAYOUT_FLAVOUR, flavour: Flavour } => ({
  type: SET_LAYOUT_FLAVOUR,
  flavour
})

export const layoutWrapperSetBottleLink = (bottleLink: (flavour: string) => string): { type: typeof LAYOUT_WRAPPER_SET_BOTTLE_LINK, bottleLink: (flavour: string) => string } => ({
  type: LAYOUT_WRAPPER_SET_BOTTLE_LINK,
  bottleLink
})

export const layoutWrapperSetPageData = (pageData: LayoutPageData): { type: typeof LAYOUT_WRAPPER_SET_PAGE_DATA, pageData: LayoutPageData } => ({
  type: LAYOUT_WRAPPER_SET_PAGE_DATA,
  pageData
})

export const setLayoutController = (): (dispatch: AppDispatch, getState: () => RootState) => void => {
  const controller = (getHistoryState() as { viewName?: string }).viewName

  return (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      search: { searchHelperVisible },
      layout: {
        deviceType: { isMobile },
        controller: _controller,
        isNavigationVisible,
        pageId
      }
    } = getState()

    // close search helper on page change
    if (controller !== _controller) {
      dispatch({
        type: SET_LAYOUT_CONTROLLER,
        controller
      })

      if (searchHelperVisible) {
        dispatch(setSearchHelperVisible(false))
      }

      // close the mobile nav on page change - some interactions inside it (e.g.
      // SmartSearchSwitch's toggle) navigate directly via replaceUrl without
      // going through NavLink's own onClick={hideNavigation}, which would
      // otherwise leave isNavigationVisible stuck true on the new page
      if (isNavigationVisible) {
        dispatch(hideNavigation())
      }

      // reset page layout and scroll to top on mobile when changing page
      if (isMobile) {
        scrollTo({ pageId: pageId ?? '', id: 0, useWindowScrollTo: true })
        dispatch(setPageView('main') as any)
        dispatch(setIsMouseAbilityOpen(false))
      }

      // if previous page was QA Content
      if (_controller === QA_CONTENT) {
        // then close mouseability drawer
        dispatch(pageToggleRight(false) as any)
      }
    }
  }
}

export const setLayoutBrowser = (): (dispatch: AppDispatch) => void => {
  const browser = detect()
  const isIE = browser.name === 'ie'
  const isIOS = browser.name === 'ios'
  let isTouchDevice = false

  switch (browser.os) {
    case 'iOS':
    case 'Android OS':
    case 'BlackBerry OS':
    case 'Windows Mobile':
    case 'Amazon OS':
    case 'Chrome OS':
      isTouchDevice = true
      break
  }

  // iPadOS (13+) requests desktop sites by default, so its user agent has no
  // "iPad"/"Mobile" token and is indistinguishable from macOS to UA sniffing.
  // Fall back to touch-capability detection to catch it.
  if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) {
    isTouchDevice = true
  }

  return (dispatch: AppDispatch) => {
    dispatch({
      type: SET_LAYOUT_BROWSER,
      browser
    })
    dispatch({
      type: SET_BROWSER_IS_IE,
      isIE
    })
    dispatch({
      type: SET_BROWSER_IS_IOS,
      isIOS
    })
    dispatch({
      type: SET_LAYOUT_IS_TOUCH_DEVICE,
      isTouchDevice
    })
  }
}

export const setLayoutHeaderHeight = (headerHeight: number): { type: typeof SET_LAYOUT_HEADER_HEIGHT, headerHeight: number } => ({
  type: SET_LAYOUT_HEADER_HEIGHT,
  headerHeight
})

export const setWindowSize = ({ windowHeight, windowWidth }: { windowHeight: number, windowWidth: number }) => {
  return (dispatch: AppDispatch, _: any) => {
    dispatch(setWindowHeight(windowHeight))
    dispatch(setWindowWidth(windowWidth) as any)
  }
}

export const setLayoutState = ({ pageId }: { pageId: string }) => {
  return (dispatch: AppDispatch, _: any) => {
    dispatch(setLayoutPageId(pageId))
    dispatch(setLayoutBrowser() as any)
    dispatch(setLayoutController() as any)
  }
}

export const setLayoutWrapperState = ({ query, bottleLink, pageData }: Pick<LayoutState, 'bottleLink' | 'pageData'> & { query: any }) => {
  return (dispatch: AppDispatch, _: any) => {
    if (query !== undefined && query !== null) {
      dispatch(setSearchQuery(query))
    }
    dispatch(layoutWrapperSetBottleLink(bottleLink))
    dispatch(layoutWrapperSetPageData(pageData))
  }
}

export const setWindowHeight = (windowHeight: number): { type: typeof SET_WINDOW_HEIGHT, windowHeight: number } => ({
  type: SET_WINDOW_HEIGHT,
  windowHeight
})

export const setWindowWidth = (windowWidth: number) => {
  return (dispatch: AppDispatch, _: any) => {
    dispatch({ type: SET_WINDOW_WIDTH, windowWidth })
    dispatch({
      type: SET_DEVICE_TYPE,
      deviceType: {
        isTablet: windowWidth <= TABLET_MAX_WIDTH,
        isMobile: windowWidth <= MOBILE_MAX_WIDTH,
        isDesktop: windowWidth >= DESKTOP_MIN_WIDTH,
        isDesktopFullscreen: windowWidth >= PAGE_MAX_WIDTH,
        isFullscreen: windowWidth <= PAGE_MAX_WIDTH,
        isLargeDesktop: windowWidth >= LARGE_DESKTOP_MIN_WIDTH
      }
    })
  }
}
